import { CACHE_STATUS_FRESH, createCacheClient } from '@noths/polaris-server-caching';
import type bunyan from 'bunyan';

import { Environment } from '../contentstackEnvironment';
import type { ContentWithStatus, StackName } from '../types';
import type { ContentstackParams } from './stack';
import { fetchStackEntry, fetchStackQuery } from './stack';

export interface Config {
  branch?: string;
  env: Environment;
  logger?: bunyan;
  stackName: StackName;
}
export interface CacheOptions {
  cacheKey: string;
  cacheTTLInSeconds?: number;
}

type ContentQuery = Record<string, string | number>;

const fetchPromises: Partial<Record<string, Promise<ContentWithStatus>>> = {};

const logError = (config: Config, error: Error) => {
  const { env, logger, stackName } = config;
  if (logger) {
    logger.error('error fetching content from Contentstack', {
      env,
      stackName,
      error,
    });
  }
};

const fetchContentWithCaching = async (
  config: Config,
  cacheOptions: CacheOptions,
  fetchFn: () => Promise<unknown>,
): Promise<ContentWithStatus> => {
  const cacheClient = createCacheClient();
  const { cacheKey, cacheTTLInSeconds } = cacheOptions;
  const cachedEntry = await cacheClient?.getByKey(cacheKey, cacheTTLInSeconds);

  if (cachedEntry && cachedEntry.status === CACHE_STATUS_FRESH) {
    return Promise.resolve({
      content: cachedEntry.data,
      status: 'fresh',
    });
  } else {
    const fetchInProgress = fetchPromises[cacheKey];
    if (fetchInProgress) {
      return fetchInProgress;
    }

    const fetchPromise = fetchFn()
      .then((content): ContentWithStatus => {
        delete fetchPromises[cacheKey];

        cacheClient?.setByKey(cacheKey, content);

        return {
          content,
          status: 'fresh',
        };
      })
      .catch((error): ContentWithStatus => {
        logError(config, error);
        delete fetchPromises[cacheKey];

        if (cachedEntry) {
          return {
            content: cachedEntry.data,
            status: 'expired',
          };
        } else {
          return {
            content: error,
            status: 'error',
          };
        }
      });

    fetchPromises[cacheKey] = fetchPromise;

    return fetchPromise;
  }
};

const fetchContentWithoutCaching = (
  config: Config,
  fetchFn: () => Promise<unknown>,
): Promise<ContentWithStatus> =>
  fetchFn()
    .then(
      (content): ContentWithStatus => ({
        content,
        status: 'fresh',
      }),
    )
    .catch((error): ContentWithStatus => {
      logError(config, error);
      return {
        content: error,
        status: 'error',
      };
    });

const allowCaching = ({ env }: Config) => env !== Environment.Preview;

const getCacheKey = ({ branch, env, stackName }: Config, contentType: string, suffix: string) =>
  [env, stackName, contentType, suffix, branch].filter(Boolean).join('-');

interface FetchContentCommonArgs {
  cacheOptions?: Omit<CacheOptions, 'cacheKey'>;
  config: Config;
  contentType: string;
  params?: ContentstackParams;
  timeout?: number;
}

export const fetchContentByEntryId = ({
  cacheOptions,
  config,
  contentType,
  entryId,
  params,
  timeout,
}: FetchContentCommonArgs & { entryId: string }) => {
  const { branch, env, stackName } = config;
  const fetchFn = () =>
    fetchStackEntry({
      branch,
      env,
      stackName,
      contentType,
      entryId,
      include: params?.include,
      only: params?.only,
      timeout,
    });

  if (allowCaching(config)) {
    const cacheConfig = {
      ...cacheOptions,
      cacheKey: getCacheKey(config, contentType, entryId),
    };

    return fetchContentWithCaching(config, cacheConfig, fetchFn);
  }

  return fetchContentWithoutCaching(config, fetchFn);
};

export const fetchContentByQuery = ({
  cacheOptions,
  config,
  contentType,
  params,
  query,
  timeout,
}: FetchContentCommonArgs & { query: ContentQuery }) => {
  const { branch, env, stackName } = config;
  const fetchFn = () =>
    fetchStackQuery({
      branch,
      env,
      stackName,
      contentType,
      query,
      include: params?.include,
      only: params?.only,
      timeout,
    });

  if (allowCaching(config)) {
    const cacheConfig = {
      ...cacheOptions,
      cacheKey: getCacheKey(config, contentType, JSON.stringify(query)),
    };

    return fetchContentWithCaching(config, cacheConfig, fetchFn);
  }

  return fetchContentWithoutCaching(config, fetchFn);
};
